// ONE PARTY'S STATEMENT, READ WHOLE FOR A WINDOW — the read behind the party page's Statement tab and its Recent activity
// (his words, 1 Nov 2026: "the party statement is not aligned with other statement+recent activity"). The route pages
// OLDEST FIRST (bff/src/routes/parties.ts: the running balance reads down the page and the last row is the box), so the
// newest entry is on the LAST page: this read walks every page of the window before anything is drawn newest first, and a
// window too long to read whole is SAID (`capped`) — never a silent cut, never the earliest rows passed off as the latest.
import { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { api, type StatementRow } from '../../../lib/api';
import { dubaiDate } from '../../../lib/format';
import { foldAsset } from '../../../lib/naming';
import { readState, type ReadState } from '../../../lib/read-state';
import { assetOrder, buildJournal, type JournalRow } from './journal';

/** The route's largest page. */
export const STATEMENT_PAGE = 500;
/** Pages read before the window is called too long: 10,000 rows — years of one party. */
export const STATEMENT_MAX_PAGES = 20;

export interface StatementWindow {
  asset?: string;
  from?: string;
  to?: string;
  undone?: boolean;
}

export interface PartyJournal {
  /** One row per transaction, newest first; the brought-forward row (txn_id null) last. */
  journal: JournalRow[];
  /** The route's rows, oldest first, every page joined — the running balance reads down them (the tie). */
  rows: StatementRow[];
  /** The statement route's own box — the party's position read the same exact way the running balance was summed. */
  box: ReadonlyArray<{ asset: string; balance: number }>;
  /** Every asset that ever touched them, busiest first — settled ones included (the Asset filter). */
  ever: string[];
  state: ReadState;
  error: unknown;
  refetch: () => void;
  /** Every page of the window read. Until then nothing newest-first is claimed. */
  complete: boolean;
  /** The window holds more than STATEMENT_MAX_PAGES pages: the rows shown are its EARLIEST, and the screen says so. */
  capped: boolean;
  /** A later page failed: the rows read are the earliest; the screen says so with Try again. */
  pageFailed: boolean;
  fetchRest: () => void;
}

/** The Dubai day `days` before today, YYYY-MM-DD — Recent activity's window. */
export function daysAgo(days: number): string {
  return dubaiDate(Date.parse(`${dubaiDate()}T00:00:00+04:00`) - days * 86_400_000);
}

/** The pages of one window as one oldest-first list: the brought-forward row of the FIRST page only (a later page's
 *  opening restates what the earlier pages already carry). */
export function joinPages(pages: ReadonlyArray<{ rows: StatementRow[] }>): StatementRow[] {
  return pages.flatMap((p, i) => (i === 0 ? p.rows : p.rows.filter((r) => r.txn_id !== null)));
}

export function usePartyJournal(slug: string, entity: string, balances: Record<string, number>, w: StatementWindow): PartyJournal {
  const asset = w.asset ?? '', from = w.from ?? '', to = w.to ?? '', undone = !!w.undone;
  // Under ['statement'], so every posting's refresh (refreshMoneyViews) reads it again; 'v2' keeps its pages apart from
  // the old console's PartyStatement, which pages 200 at a time under the same prefix.
  const q = useInfiniteQuery({
    queryKey: ['statement', slug, entity, 'v2', asset, from, to, undone],
    queryFn: ({ pageParam }) => api.partyStatement(slug, {
      entity, asset: asset || undefined, from: from || undefined, to: to || undefined,
      limit: STATEMENT_PAGE, cursor: pageParam || undefined, include_undone: undone,
    }),
    initialPageParam: '',
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const pages = q.data?.pages.length ?? 0;
  const capped = !!q.hasNextPage && pages >= STATEMENT_MAX_PAGES;
  const pageFailed = q.isFetchNextPageError;
  // WALK TO THE NEWEST PAGE, one page at a time, while nothing failed and the cap is not reached.
  useEffect(() => {
    if (q.hasNextPage && !q.isFetchingNextPage && !q.isFetchNextPageError && pages < STATEMENT_MAX_PAGES) void q.fetchNextPage();
  }, [q.hasNextPage, q.isFetchingNextPage, q.isFetchNextPageError, pages]);   // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => joinPages(q.data?.pages ?? []), [q.data]);
  const journal = useMemo(() => {
    const first = q.data?.pages[0];
    if (!first) return [];
    const order = assetOrder(balances, first.box ?? [], first.assets ?? []);
    return buildJournal(rows, order);
  }, [rows, q.data, balances]);
  const first = q.data?.pages[0];

  return {
    journal,
    rows,
    box: first?.box ?? [],
    ever: (first?.assets ?? []).map((a) => a.asset),
    state: readState(q),
    error: q.error,
    refetch: () => { void q.refetch(); },
    complete: !!q.data && !q.hasNextPage,
    capped,
    pageFailed,
    fetchRest: () => { void q.fetchNextPage(); },
  };
}

/** The search box over the rows read: the reference, the deal and the ledger's own sentence, any case. */
export function matchesSearch(r: JournalRow, needle: string): boolean {
  const n = needle.trim().toLowerCase();
  if (!n) return true;
  return r.legs.some((l) => [l.reference, l.deal_id, l.linked_deal_id, l.words].some((v) => (v ?? '').toLowerCase().includes(n)));
}

/** The Asset filter's list: the party's balances, then every asset the statement says ever touched them and its box —
 *  a currency settled back to square still has history to filter on (Astra, 1 Nov 2026). One option per folded code. */
export function statementAssets(balanceAssets: readonly string[], ever: readonly string[], box: ReadonlyArray<{ asset: string }>): string[] {
  return [...new Set([...balanceAssets, ...ever, ...box.map((b) => b.asset)].map((a) => foldAsset(a)))];
}
